import React, { ReactElement, useEffect, useState } from "react";

import styled from "styled-components";

import { Button } from "../../components/Button";
import { ChooseV2, ChooseV2Option } from "../../components/ChooseV2";
import { Dialog } from "../../components/Dialog";
import { IconButton } from "../../components/IconButton";
import {
  CancelCrossIcon,
  CommandLineIcon,
  CopyIcon,
  HashIcon,
} from "../../components/Icons";
import { SkeletonLoader } from "../../components/Loaders";
import { Tooltip } from "../../components/Tooltip";
import { Body2, Caption1, Header3 } from "../../components/Typography";

/**
 * "Linking and Commands" button — a plain design-system secondary button (small,
 * rounded, no icon, no AI gradient: this is not an AI feature). Clicking it opens
 * a read-only reference dialog.
 *
 * This mock mirrors the shipped `activecollab-next` implementation
 * (`src/components/tasks/SourceCode/LinkingAndCommandsButton.jsx` +
 * `LinkingAndCommandsDialog.jsx`, task #457754): a 640px dialog with a short
 * pitch, a centered ChooseV2 to switch conventions (Linking default), and two
 * tabs. Linking shows the reference forms for this task and a copyable
 * `git checkout -b …` suggestion; Commands shows an API-shaped table of action
 * words and slash commands, plus a fixed example commit. Both the reference
 * guide and the suggested-branch request are represented by short skeleton
 * loads, the way the real dialog fetches them once it is opened.
 */
const SANS =
  '-apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", Arial, sans-serif';
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** The example task these instructions are written against. */
const TASK_ID = 4231;
/** Task URL, in production built with `~/utils/urls` `getTaskUrl`. */
const TASK_URL = `https://app.activecollab.com/1/projects/2110/tasks/${TASK_ID}`;
/**
 * Suggested branch name for this task — in production it is resolved per repo
 * via GET .../tasks/{task_id}/source/suggest-branch-name. Mocked here.
 */
const SUGGESTED_BRANCH = `ilija/${TASK_ID}-add-cacak-region-support`;

/** POSIX-safe single-quote escaping for a value dropped into a shell command. */
const shellQuote = (value: string): string =>
  `'${value.replace(/'/g, "'\\''")}'`;

/* ------------------------------------------------------------------ */
/* Reference-guide data — shaped like the GET .../source/reference-guide */
/* payload the real dialog is driven by: a map of action words plus a    */
/* flat list of slash commands. Descriptions are localised server-side;  */
/* tokens and examples never translate.                                  */
/* ------------------------------------------------------------------ */

interface ActionWord {
  primary: string;
  example: string;
  description: string;
  aliases?: string[];
}

interface SlashCommand {
  name: string;
  syntax: string;
  example: string;
  description: string;
  aliases?: string[];
}

/** Action words act on the linked task from a title, commit, or PR body. */
const ACTION_WORDS: Record<string, ActionWord> = {
  close: {
    primary: "Close",
    example: `Close #${TASK_ID}`,
    description: "Closes the task when its pull request is merged.",
    aliases: ["Closes", "Fixes", "Fixed", "Resolves", "Resolved"],
  },
};

/** Slash commands act on the task from a commit message or PR description. */
const COMMANDS: SlashCommand[] = [
  {
    name: "spend",
    syntax: "/spend {duration} [of {job type}]",
    example: "/spend 1h 30m of Design",
    description: "Logs the time you spent on the task.",
  },
  {
    name: "estimate",
    syntax: "/estimate {duration}",
    example: "/estimate 4h",
    description: "Sets the task's time estimate.",
  },
  {
    name: "comment",
    syntax: "/comment {text}",
    example: "/comment Deployed to staging",
    description: "Posts a comment on the task.",
  },
  {
    name: "done",
    syntax: "/done",
    example: "/done",
    description: "Marks the task complete.",
    aliases: ["/complete"],
  },
  {
    name: "reopen",
    syntax: "/reopen",
    example: "/reopen",
    description: "Reopens a completed task.",
  },
  {
    name: "assign",
    syntax: "/assign @{member}",
    example: "/assign @ilija",
    description: "Assigns the task to a team member.",
  },
  {
    name: "label",
    syntax: "/label {name}",
    example: "/label In Progress",
    description: "Adds a label to the task.",
  },
];

interface TableRow {
  key: string;
  example: string;
  format?: string;
  description: string;
  aliases?: string[];
}

/** Action words first, then the slash commands — the shipped row order. */
const TABLE_ROWS: TableRow[] = [
  ...Object.values(ACTION_WORDS).map((word) => ({
    key: `action-${word.primary}`,
    example: word.example,
    description: word.description,
    aliases: word.aliases,
  })),
  ...COMMANDS.map((command) => ({
    key: `command-${command.name}`,
    example: command.example,
    format: command.syntax,
    description: command.description,
    aliases: command.aliases,
  })),
];

/**
 * Illustrative commit message: closes the task on the subject line, logs time,
 * leaves a note. Fixed sample text — only the task id is interpolated, and
 * durations are printed verbatim (never reformatted).
 */
const EXAMPLE_COMMIT = `Fix ranks reset when a contract is archived, closes #${TASK_ID}

/spend 1h 30m
/comment Verified on staging, ranks hold after archive`;

/* ------------------------------------------------------------------ */
/* Trigger button — plain DS secondary button, rounded to the compact   */
/* "Suggest Subtasks" shape (6px), no icon, no gradient.                 */
/* ------------------------------------------------------------------ */

const TriggerButton = styled(Button)`
  && {
    border-radius: 6px;
  }
`;

/** Wider than the default 540px dialog, matching the shipped 640px width. */
const StyledDialog = styled(Dialog)`
  && {
    width: 640px;
    max-width: calc(100vw - 64px);
  }
`;

const StyledTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const StyledBody = styled.div`
  font-family: ${SANS};
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;

  /* Inline code chip — mirrors the app InlineCode (mono, theme-200 fill). */
  .code {
    font-family: ${MONO};
    background: var(--color-theme-200);
    color: var(--color-theme-900);
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 12px;
  }

  /* Bold, primary-coloured lead-in term inside a secondary body line. */
  .lc-term {
    font-weight: 600;
    color: var(--color-theme-900);
  }

  /* Lead rows under the pitch. */
  .lc-leads {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .lc-lead {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
  .lc-lead svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    color: var(--color-theme-600);
  }

  /* ChooseV2 root is passed .lc-choosectl; its inner flex row is centered. */
  .lc-choosectl > div {
    justify-content: center;
  }

  .lc-tabbody {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .lc-commands {
    gap: 16px;
  }
  .lc-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .lc-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;
    padding-left: 20px;
    list-style: disc;
  }

  /* Commands table — two-column grid, action words then slash commands. */
  .lc-table {
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 24px;
  }
  .lc-th {
    padding-bottom: 8px;
    border-bottom: 1px solid var(--border-primary);
  }
  .lc-td {
    padding: 12px 0;
    border-bottom: 1px solid var(--border-primary);
  }
  .lc-format {
    display: block;
    margin-top: 4px;
    font-family: ${MONO};
  }
  .lc-aliases {
    margin-top: 12px;
  }

  /* Loading skeletons for the reference guide and the suggested branch. */
  .lc-skeleton {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .lc-skel {
    border-radius: 6px;
  }
  .lc-skel-line {
    height: 20px;
  }
  .lc-skel-block {
    height: 36px;
    width: 100%;
  }
  .lc-skel-2-3 {
    width: 66%;
  }
  .lc-skel-1-2 {
    width: 50%;
  }
  .lc-branch-skel {
    width: 100%;
    height: 36px;
    border-radius: 6px;
  }
`;

/* Copyable code snippet — mirrors the shared CopySnippet: a theme-200 <pre>
   with a copy control that reveals on hover (hidden when hideCopyButton). */
const StyledSnippet = styled.div`
  position: relative;

  pre {
    margin: 0;
    background: var(--color-theme-200);
    color: var(--color-theme-900);
    border-radius: 6px;
    padding: 12px 16px;
    overflow-x: auto;
    font-family: ${MONO};
    font-size: 13px;
    line-height: 1.5;
  }
  .lc-copy-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }
  &:hover .lc-copy-btn {
    opacity: 1;
  }
  .lc-copy-btn svg {
    width: 16px;
    height: 16px;
  }
`;

type LcTab = "linking" | "commands";

const TAB_OPTIONS: ChooseV2Option[] = [
  { id: "linking", name: "Linking" },
  { id: "commands", name: "Commands" },
];

interface CopySnippetProps {
  code: string;
  hideCopyButton?: boolean;
}

const CopySnippet = ({
  code,
  hideCopyButton = false,
}: CopySnippetProps): ReactElement => {
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    // eslint-disable-next-line compat/compat
    void navigator.clipboard?.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <StyledSnippet>
      <pre>
        <code>{code}</code>
      </pre>
      {!hideCopyButton && (
        <Tooltip title={copied ? "Copied!" : "Copy"}>
          <button
            type="button"
            className="lc-copy-btn"
            onClick={copy}
            aria-label="Copy command"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 4,
              display: "inline-flex",
              color: "var(--color-theme-600)",
            }}
          >
            <CopyIcon />
          </button>
        </Tooltip>
      )}
    </StyledSnippet>
  );
};

interface LinkingTabProps {
  branchLoading: boolean;
  branch: string | null;
}

const LinkingTab = ({
  branchLoading,
  branch,
}: LinkingTabProps): ReactElement => {
  const command = branch ? `git checkout -b ${shellQuote(branch)}` : "";

  return (
    <div className="lc-tabbody">
      <div className="lc-block">
        <Body2 color="secondary">
          Mention the task in your git activity and it links to this task on its
          own:
        </Body2>
        <ul className="lc-list">
          <li>
            <Body2 color="secondary">
              <span className="lc-term">In a branch name:</span> the raw task ID{" "}
              <span className="code">{TASK_ID}</span>
            </Body2>
          </li>
          <li>
            <Body2 color="secondary">
              <span className="lc-term">
                In a title, commit message, or description:
              </span>{" "}
              <span className="code">#{TASK_ID}</span> or{" "}
              <span className="code">AC-{TASK_ID}</span>
            </Body2>
          </li>
          <li>
            <Body2 color="secondary">
              <span className="lc-term">Anywhere:</span> the task URL{" "}
              <span className="code">{TASK_URL}</span>
            </Body2>
          </li>
        </ul>
      </div>

      {branchLoading || command ? (
        <div className="lc-block">
          <Body2 color="secondary">
            Give the branch a name with the task number in it and it links the
            moment you push. Create one from the suggested name:
          </Body2>
          {branchLoading ? (
            <SkeletonLoader className="lc-branch-skel" />
          ) : (
            <CopySnippet code={command} />
          )}
        </div>
      ) : null}
    </div>
  );
};

const CommandsTab = (): ReactElement => (
  <div className="lc-tabbody lc-commands">
    <Body2 color="secondary">
      The table below lists action words and slash commands that you can use in
      a commit message or a pull request description to act on the linked task.
      Example:
    </Body2>
    <CopySnippet code={EXAMPLE_COMMIT} hideCopyButton />
    <div className="lc-table">
      <Caption1 color="tertiary" className="lc-th">
        Command
      </Caption1>
      <Caption1 color="tertiary" className="lc-th">
        What it does
      </Caption1>
      {TABLE_ROWS.map((row) => (
        <React.Fragment key={row.key}>
          <div className="lc-td">
            <span className="code">{row.example}</span>
            {row.format ? (
              <Caption1 color="tertiary" className="lc-format">
                {row.format}
              </Caption1>
            ) : null}
          </div>
          <div className="lc-td">
            <Body2 color="secondary">{row.description}</Body2>
            {row.aliases?.length ? (
              <Body2 color="secondary" className="lc-aliases">
                Aliases: {row.aliases.join(", ")}
              </Body2>
            ) : null}
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

export const LinkingCommandsButton = (): ReactElement => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<LcTab>("linking");
  const [guideLoading, setGuideLoading] = useState(true);
  const [branchLoading, setBranchLoading] = useState(true);
  const [suggestedBranch, setSuggestedBranch] = useState<string | null>(null);

  // Mock the two endpoint loads the real dialog fires once it opens: the
  // reference guide resolves first (~0.6s), then the suggested branch name
  // (~1.1s), each showing its own skeleton until it lands.
  useEffect(() => {
    if (!open) return undefined;
    setGuideLoading(true);
    setBranchLoading(true);
    setSuggestedBranch(null);
    const guideTimer = window.setTimeout(() => setGuideLoading(false), 600);
    const branchTimer = window.setTimeout(() => {
      setSuggestedBranch(SUGGESTED_BRANCH);
      setBranchLoading(false);
    }, 1100);
    return () => {
      window.clearTimeout(guideTimer);
      window.clearTimeout(branchTimer);
    };
  }, [open]);

  const openDialog = (): void => {
    setTab("linking");
    setOpen(true);
  };

  return (
    <>
      <TriggerButton
        type="button"
        variant="secondary"
        size="small"
        onClick={openDialog}
      >
        Linking and Commands
      </TriggerButton>

      <StyledDialog
        open={open}
        onClose={() => setOpen(false)}
        enableBackgroundClick
      >
        <Dialog.Title disableDefaultHeading>
          <StyledTitleRow>
            <Header3>Linking and Commands</Header3>
            <IconButton
              type="button"
              variant="text gray"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <CancelCrossIcon />
            </IconButton>
          </StyledTitleRow>
        </Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <StyledBody>
            {guideLoading ? (
              <div className="lc-skeleton">
                <SkeletonLoader className="lc-skel lc-skel-line lc-skel-2-3" />
                <SkeletonLoader className="lc-skel lc-skel-block" />
                <SkeletonLoader className="lc-skel lc-skel-block" />
                <SkeletonLoader className="lc-skel lc-skel-line lc-skel-1-2" />
              </div>
            ) : (
              <>
                <Body2>
                  Keep a task in sync with your code without leaving your
                  editor.
                </Body2>
                <div className="lc-leads">
                  <div className="lc-lead">
                    <HashIcon />
                    <Body2 color="secondary">
                      <span className="lc-term">Linking:</span> Mention a task
                      in a branch, pull request, or commit and they will be
                      connected automatically.
                    </Body2>
                  </div>
                  <div className="lc-lead">
                    <CommandLineIcon />
                    <Body2 color="secondary">
                      <span className="lc-term">Commands:</span> Add short
                      instructions in a commit or pull request description to
                      act on the task: log time, close it, or leave a comment.
                    </Body2>
                  </div>
                </div>

                <ChooseV2
                  className="lc-choosectl"
                  options={TAB_OPTIONS}
                  selected={[tab]}
                  onChange={(ids) => setTab(ids[0] as LcTab)}
                />

                {tab === "linking" ? (
                  <LinkingTab
                    branchLoading={branchLoading}
                    branch={suggestedBranch}
                  />
                ) : (
                  <CommandsTab />
                )}
              </>
            )}
          </StyledBody>
        </Dialog.Content>
      </StyledDialog>
    </>
  );
};

LinkingCommandsButton.displayName = "LinkingCommandsButton";
